// vigil – MUI v9 Theme (heller Modus). 1:1 aus docs/design/mui-theme.md übernommen
// (Entwickler-Briefing Kapitel 7: "Die Oberfläche ist abgestimmt. Die Umsetzung
// übernimmt sie, statt sie neu zu entwerfen."). Werte entsprechen dem Prototyp vigil.dc.html.
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    cta: Palette['primary'];
  }
  interface PaletteOptions {
    cta?: PaletteOptions['primary'];
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    cta: true;
  }
}

export const brand = {
  deepNavy: '#001D33',
  navy: '#003953',
  mint: '#67CB9F',
  brightMint: '#68E3A8',
  teal: '#008388',
  paperWhite: '#F7FBFD',
  cloud: '#E7EEF1',
  slate: '#8FA3AC',
  fog: '#B9C4C8',
  orange: '#CF6E2C',
};

// Chip-Farben je Status/Priorität. Bedeutung immer zusätzlich über Icon + Text (BR-8).
export const statusChip = {
  frei: { icon: 'CheckCircle', fg: '#1E6E4E', bg: '#E6F7EF', border: '#A8E2C6', text: '[✓] freigegeben' },
  auflagen: {
    icon: 'Rule',
    fg: '#00666A',
    bg: '#E0F2F2',
    border: '#9FD3D4',
    text: '[✓*] freigegeben mit Auflagen',
  },
  nicht: { icon: 'Block', fg: '#B42318', bg: '#FDECEA', border: '#F4B8B2', text: '[✗] nicht freigegeben' },
  pruefung: {
    icon: 'HourglassTop',
    fg: '#003953',
    bg: '#E7EEF1',
    border: '#B9C4C8',
    text: '[…] in Prüfung',
  },
  neuUnbewertet: {
    icon: 'NewReleases',
    fg: '#7A5200',
    bg: '#FFF4D6',
    border: '#EBCB73',
    text: '[neu] unbewertet',
  },
} as const;

export const priorityChip = {
  hoch: { icon: 'PriorityHigh', fg: '#B42318', bg: '#FDECEA', border: '#F4B8B2', text: 'hoch' },
  mittel: { icon: 'Warning', fg: '#7A5200', bg: '#FFF4D6', border: '#EBCB73', text: 'mittel' },
  niedrig: { icon: 'ArrowDownward', fg: '#3E5560', bg: '#F1F5F7', border: '#B9C4C8', text: 'niedrig' },
  info: { icon: 'Info', fg: '#2D5D78', bg: '#EAF2F7', border: '#B5CEDC', text: 'info' },
} as const;

export const diff = {
  removed: { bg: '#FDECEA', fg: '#7A1A12', textDecoration: 'line-through' },
  added: { bg: '#DDF5E9', fg: '#0F4F35', textDecoration: 'underline' },
};

export const marking = {
  hoverOutline: `2px solid ${brand.teal}`,
  selectedOutline: '2px solid #1E6E4E',
  selectedFill: '#E3F9EE',
  savedOutline: `1px solid ${brand.teal}`,
  hiddenFill: 'repeating-linear-gradient(135deg,#E7EEF1 0 6px,#FFFFFF 6px 12px)',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: brand.navy, dark: brand.deepNavy, light: '#2D5D78', contrastText: '#FFFFFF' },
    secondary: { main: brand.teal, dark: '#00666A', light: '#E0F2F2', contrastText: '#FFFFFF' },
    error: { main: '#B42318', dark: '#7A1A12', light: '#FDECEA', contrastText: '#FFFFFF' },
    warning: { main: '#E0A800', dark: '#7A5200', light: '#FFF4D6', contrastText: brand.deepNavy },
    success: { main: brand.mint, dark: '#1E6E4E', light: '#E6F7EF', contrastText: brand.deepNavy },
    info: { main: '#2D5D78', dark: '#1F4459', light: '#EAF2F7', contrastText: '#FFFFFF' },
    cta: { main: brand.orange, dark: '#B85F22', light: '#F3D9C7', contrastText: brand.deepNavy },
    text: { primary: brand.navy, secondary: '#5B6F78', disabled: brand.fog },
    background: { default: brand.paperWhite, paper: '#FFFFFF' },
    divider: brand.cloud,
    grey: { 100: brand.paperWhite, 200: brand.cloud, 400: brand.fog, 500: brand.slate },
  },
  spacing: 8,
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    h1: { fontSize: 28, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01em', color: brand.deepNavy },
    h2: { fontSize: 18, fontWeight: 600, lineHeight: 1.3, color: brand.deepNavy },
    h3: { fontSize: 16, fontWeight: 600, lineHeight: 1.35, color: brand.deepNavy },
    subtitle1: { fontSize: 15, fontWeight: 600 },
    body1: { fontSize: 14, lineHeight: 1.6 },
    body2: { fontSize: 13, lineHeight: 1.55 },
    caption: { fontSize: 12, lineHeight: 1.5, color: '#5B6F78' },
    overline: { fontFamily: '"IBM Plex Mono", monospace', fontSize: 12, fontWeight: 500, letterSpacing: '0.06em' },
    button: { fontSize: 14, fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiAppBar: { defaultProps: { elevation: 1 }, styleOverrides: { root: { backgroundColor: brand.deepNavy, height: 64 } } },
    // root reserves the width in the flex layout: the permanent paper is position:fixed and takes no space itself
    MuiDrawer: { styleOverrides: { root: { width: 248, flexShrink: 0 }, paper: { width: 248, borderRight: `1px solid ${brand.cloud}` } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${brand.cloud}`,
          boxShadow: '0 1px 2px rgba(0,29,51,.06), 0 2px 8px rgba(0,29,51,.04)',
        },
      },
    },
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { borderRadius: 8, minHeight: 40 } } },
    MuiChip: {
      styleOverrides: {
        root: { height: 24, borderRadius: 12, fontSize: 12, fontWeight: 500, borderWidth: 1, borderStyle: 'solid' },
        icon: { fontSize: 16 },
      },
    },
    MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8 }, notchedOutline: { borderColor: brand.fog } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500, minHeight: 48 } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: brand.deepNavy } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 12 } } },
    MuiPopover: { styleOverrides: { paper: { borderRadius: 12 } } },
  },
});

// Monospace für Modell-IDs, Fassungskennungen, Datumswerte in Tabellen (BR-12):
export const mono = { fontFamily: '"IBM Plex Mono", monospace' };
